import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { 
  getSyncStatus, 
  getSyncQueue, 
  removeFromSyncQueue, 
  updateLastSyncTime,
  clearAllOfflineData 
} from '@/services/offlineDataService';
import API from '@/utils/api';

const OfflineContext = createContext(null);

/**
 * OfflineProvider - Manages offline state and service worker
 */
export const OfflineProvider = ({ children }) => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isOfflineReady, setIsOfflineReady] = useState(false);
  const [queuedRequests, setQueuedRequests] = useState(0);
  const [swRegistration, setSwRegistration] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [syncStatus, setSyncStatus] = useState(getSyncStatus());

  // Register service worker
  useEffect(() => {
    if ('serviceWorker' in navigator && navigator.serviceWorker) {
      navigator.serviceWorker.register('/service-worker.js')
        .then((registration) => {
          console.log('Service Worker registered:', registration);
          setSwRegistration(registration);
          setIsOfflineReady(true);
          
          // Check for updates
          registration.addEventListener('updatefound', () => {
            const newWorker = registration.installing;
            newWorker.addEventListener('statechange', () => {
              if (newWorker.state === 'installed' && navigator.serviceWorker?.controller) {
                // New version available
                toast.info('App update available. Refresh to update.', {
                  action: {
                    label: 'Refresh',
                    onClick: () => window.location.reload(),
                  },
                });
              }
            });
          });
        })
        .catch((error) => {
          console.error('Service Worker registration failed:', error);
        });

      // Listen for messages from service worker
      navigator.serviceWorker.addEventListener('message', (event) => {
        if (event.data.type === 'SYNC_COMPLETE') {
          const { synced, failed } = event.data;
          if (synced > 0) {
            toast.success(`Synced ${synced} offline change${synced !== 1 ? 's' : ''}`);
          }
          if (failed > 0) {
            toast.error(`Failed to sync ${failed} change${failed !== 1 ? 's' : ''}`);
          }
          updateQueuedRequests();
        }
      });
    }
  }, []);

  // Handle online/offline status
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      toast.success('You\'re back online!');
      
      // Trigger sync if available
      if (swRegistration && 'sync' in swRegistration) {
        swRegistration.sync.register('sync-offline-requests');
      }
    };

    const handleOffline = () => {
      setIsOnline(false);
      toast.warning('You\'re offline. Changes will be saved locally.');
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [swRegistration]);

  // Update queued requests count
  const updateQueuedRequests = useCallback(async () => {
    if ('serviceWorker' in navigator && navigator.serviceWorker?.controller) {
      const messageChannel = new MessageChannel();
      messageChannel.port1.onmessage = (event) => {
        setQueuedRequests(event.data.queuedRequests || 0);
      };
      navigator.serviceWorker.controller.postMessage(
        { type: 'GET_OFFLINE_STATUS' },
        [messageChannel.port2]
      );
    }
  }, []);

  useEffect(() => {
    updateQueuedRequests();
    const interval = setInterval(updateQueuedRequests, 30000);
    return () => clearInterval(interval);
  }, [updateQueuedRequests]);

  // Clear all cached data
  const clearCache = useCallback(async () => {
    if ('serviceWorker' in navigator && navigator.serviceWorker?.controller) {
      return new Promise((resolve) => {
        const messageChannel = new MessageChannel();
        messageChannel.port1.onmessage = (event) => {
          if (event.data.cleared) {
            toast.success('Cache cleared');
            resolve(true);
          }
        };
        navigator.serviceWorker.controller.postMessage(
          { type: 'CLEAR_CACHE' },
          [messageChannel.port2]
        );
      });
    }
    return false;
  }, []);

  // Force update service worker
  const forceUpdate = useCallback(() => {
    if (swRegistration && swRegistration.waiting) {
      swRegistration.waiting.postMessage({ type: 'SKIP_WAITING' });
      window.location.reload();
    }
  }, [swRegistration]);

  // Refresh sync status
  const refreshSyncStatus = useCallback(() => {
    setSyncStatus(getSyncStatus());
  }, []);

  // Sync a single operation
  const syncOperation = useCallback(async (operation) => {
    try {
      const { method, endpoint, data } = operation;
      
      let response;
      switch (method.toUpperCase()) {
        case 'POST':
          response = await API.post(endpoint, data);
          break;
        case 'PUT':
          response = await API.put(endpoint, data);
          break;
        case 'PATCH':
          response = await API.patch(endpoint, data);
          break;
        case 'DELETE':
          response = await API.delete(endpoint);
          break;
        default:
          throw new Error(`Unsupported method: ${method}`);
      }
      
      return { success: true, response: response.data };
    } catch (error) {
      console.error('Sync operation failed:', error);
      return { 
        success: false, 
        error: error.response?.data?.detail || error.message 
      };
    }
  }, []);

  // Sync all pending operations
  const syncAll = useCallback(async () => {
    if (syncing || !navigator.onLine) return { successCount: 0, failCount: 0 };
    
    const queue = getSyncQueue();
    if (queue.length === 0) return { successCount: 0, failCount: 0 };
    
    setSyncing(true);
    let successCount = 0;
    let failCount = 0;
    
    for (const operation of queue) {
      const result = await syncOperation(operation);
      
      if (result.success) {
        removeFromSyncQueue(operation.id);
        successCount++;
      } else {
        failCount++;
      }
    }
    
    updateLastSyncTime();
    refreshSyncStatus();
    setSyncing(false);
    
    if (successCount > 0) {
      toast.success(`Synced ${successCount} offline change${successCount !== 1 ? 's' : ''}`);
    }
    if (failCount > 0) {
      toast.error(`Failed to sync ${failCount} change${failCount !== 1 ? 's' : ''}`);
    }
    
    return { successCount, failCount };
  }, [syncing, syncOperation, refreshSyncStatus]);

  // Auto-sync when coming back online
  useEffect(() => {
    if (isOnline && syncStatus.hasPendingChanges) {
      setTimeout(syncAll, 1500);
    }
  }, [isOnline, syncStatus.hasPendingChanges, syncAll]);

  // Refresh sync status periodically
  useEffect(() => {
    const interval = setInterval(refreshSyncStatus, 30000);
    return () => clearInterval(interval);
  }, [refreshSyncStatus]);

  // Clear all offline data including local storage
  const clearAllData = useCallback(async () => {
    await clearCache();
    clearAllOfflineData();
    refreshSyncStatus();
  }, [clearCache, refreshSyncStatus]);

  const value = {
    isOnline,
    isOfflineReady,
    queuedRequests,
    clearCache,
    forceUpdate,
    syncing,
    syncStatus,
    syncAll,
    refreshSyncStatus,
    clearAllData,
  };

  return (
    <OfflineContext.Provider value={value}>
      {children}
    </OfflineContext.Provider>
  );
};

/**
 * Hook to access offline state
 */
export const useOffline = () => {
  const context = useContext(OfflineContext);
  if (!context) {
    throw new Error('useOffline must be used within an OfflineProvider');
  }
  return context;
};

export default OfflineContext;
